import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BookOpen, ChevronDown, ChevronUp, Loader2, Pencil, Plus, Search, Trash2,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import ImageUpload from "./ImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const EMPTY_FORM = {
  name: "", short_name: "", duration: "", description: "",
  category: "Computer Courses", image: "", active: true, display_order: "",
};

const sortCourses = (list) =>
  [...list].sort((a, b) => {
    const oa = a.display_order ?? 9999;
    const ob = b.display_order ?? 9999;
    return oa !== ob ? oa - ob : (a.name || "").localeCompare(b.name || "");
  });

export default function WebCoursesTab() {
  const [courses, setCourses] = useState(null);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // full course doc; null = new course
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    const { data } = await api.get("/courses");
    setCourses(sortCourses(data));
  };
  useEffect(() => {
    load();
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, display_order: (courses?.length || 0) + 1 });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      short_name: c.short_name || "",
      duration: c.duration || "",
      description: c.description || "",
      category: c.category || "Computer Courses",
      image: c.image || "",
      active: c.active !== false,
      display_order: c.display_order ?? "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Course name is required");
      return;
    }
    setSaving(true);
    try {
      const fields = {
        name: form.name.trim(),
        short_name: form.short_name.trim(),
        duration: form.duration.trim(),
        description: form.description,
        category: form.category.trim() || "Computer Courses",
        image: form.image || "",
        active: !!form.active,
        display_order: form.display_order === "" ? null : Number(form.display_order),
      };
      if (editing) {
        // merge — preserves subjects/terms/academic config untouched
        await api.put(`/courses/${editing.id}`, { ...editing, ...fields });
      } else {
        await api.post("/courses", { ...fields, subjects: [], course_type: "", eligibility: "" });
      }
      toast.success(editing ? "Course updated" : "Course added");
      setDialogOpen(false);
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    await api.put(`/courses/${c.id}`, { ...c, active: c.active === false });
    setCourses((list) => sortCourses(list.map((x) => (x.id === c.id ? { ...x, active: c.active === false } : x))));
    toast.success(c.active === false ? "Course is now Active (visible on website)" : "Course is now Inactive (hidden from website)");
  };

  const move = async (idx, dir) => {
    const arr = courses.map((c, i) => ({ ...c, display_order: i + 1 }));
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx].display_order, arr[j].display_order] = [arr[j].display_order, arr[idx].display_order];
    setCourses(sortCourses(arr));
    await Promise.all([
      api.put(`/courses/${arr[idx].id}`, arr[idx]),
      api.put(`/courses/${arr[j].id}`, arr[j]),
    ]);
    toast.success("Display order updated");
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/courses/${deleteTarget.id}`);
      toast.success("Course deleted");
      setDeleteTarget(null);
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (courses === null) return <div className="p-6 text-slate-400 text-sm">Loading…</div>;

  const q = search.trim().toLowerCase();
  const visible = courses.filter(
    (c) =>
      !q ||
      (c.name || "").toLowerCase().includes(q) ||
      (c.duration || "").toLowerCase().includes(q) ||
      (c.short_name || "").toLowerCase().includes(q)
  );

  return (
    <div data-testid="ws-courses">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="wc-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search courses by name or duration…"
            className="pl-9 h-10"
          />
        </div>
        <Button onClick={openAdd} data-testid="wc-add" className="bg-blue-800 hover:bg-blue-900 h-10 sm:ml-auto">
          <Plus size={15} className="mr-1.5" /> Add Course
        </Button>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        These are the same courses used for certificates/marksheets — this section manages how they appear on the
        public website. Subjects & terms stay under Templates → Courses.
      </p>

      {visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-12 text-center text-sm text-slate-400" data-testid="wc-empty">
          <BookOpen className="mx-auto mb-2 text-slate-300" size={26} />
          {q ? "No courses match your search." : "No courses yet — click “+ Add Course”."}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="hidden md:grid grid-cols-[44px_1fr_110px_110px_90px_190px] gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <span>#</span><span>Course Name</span><span>Duration</span><span>Status</span><span>Order</span><span className="text-right">Actions</span>
          </div>
          {visible.map((c) => {
            const idx = courses.findIndex((x) => x.id === c.id);
            const active = c.active !== false;
            return (
              <div key={c.id} data-testid={`wc-row-${c.id}`}
                className="grid grid-cols-[44px_1fr_auto] md:grid-cols-[44px_1fr_110px_110px_90px_190px] gap-3 items-center px-4 py-3 border-b border-slate-100 last:border-0">
                <div className="w-9 h-9 rounded-md bg-slate-100 overflow-hidden grid place-items-center text-blue-800">
                  {c.image ? (
                    <img src={c.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <BookOpen size={16} />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 truncate">
                    {c.name}
                    {c.short_name && (
                      <span className="ml-1.5 text-[10px] font-semibold uppercase bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                        {c.short_name}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 md:hidden">{c.duration}</div>
                  {c.category && <div className="hidden md:block text-[11px] text-slate-400">{c.category}</div>}
                </div>
                <div className="hidden md:block text-sm text-slate-600">{c.duration || "—"}</div>
                <div className="hidden md:block">
                  <button data-testid={`wc-status-${c.id}`} onClick={() => toggleActive(c)}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-soft ${
                      active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-slate-200 text-slate-500 hover:bg-slate-300"
                    }`}
                    title="Click to toggle Active / Inactive">
                    {active ? "Active" : "Inactive"}
                  </button>
                </div>
                <div className="hidden md:flex items-center gap-1">
                  <button data-testid={`wc-up-${c.id}`} onClick={() => move(idx, -1)} disabled={idx === 0}
                    className="p-1 rounded border border-slate-200 text-slate-500 hover:border-blue-300 disabled:opacity-30" title="Move up">
                    <ChevronUp size={13} />
                  </button>
                  <button data-testid={`wc-down-${c.id}`} onClick={() => move(idx, 1)} disabled={idx === courses.length - 1}
                    className="p-1 rounded border border-slate-200 text-slate-500 hover:border-blue-300 disabled:opacity-30" title="Move down">
                    <ChevronDown size={13} />
                  </button>
                  <span className="text-xs text-slate-400 ml-1">{idx + 1}</span>
                </div>
                <div className="flex items-center gap-1 justify-end">
                  <button data-testid={`wc-toggle-${c.id}`} onClick={() => toggleActive(c)}
                    className={`md:hidden text-[11px] font-semibold px-2 py-1 rounded-full mr-1 ${active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                    {active ? "Active" : "Inactive"}
                  </button>
                  <Button size="sm" variant="outline" data-testid={`wc-edit-${c.id}`} onClick={() => openEdit(c)} className="h-8">
                    <Pencil size={13} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`wc-del-${c.id}`} onClick={() => setDeleteTarget(c)}
                    className="h-8 text-red-600 border-red-200 hover:bg-red-50">
                    <Trash2 size={13} className="mr-1" /> Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="wc-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit — ${editing.name}` : "Add New Course"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div>
              <Label>Course Name *</Label>
              <Input data-testid="wc-name" value={form.name} className="mt-1.5 h-10"
                placeholder="e.g., Diploma in Computer Applications"
                onChange={(e) => upd("name", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duration</Label>
                <Input data-testid="wc-duration" value={form.duration} className="mt-1.5 h-10"
                  placeholder="e.g., 1 Year" onChange={(e) => upd("duration", e.target.value)} />
              </div>
              <div>
                <Label>Short Name</Label>
                <Input data-testid="wc-short" value={form.short_name} className="mt-1.5 h-10"
                  placeholder="e.g., DCA" onChange={(e) => upd("short_name", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Course Category</Label>
                <Input data-testid="wc-category" value={form.category} className="mt-1.5 h-10" list="wc-category-list"
                  placeholder="Computer Courses" onChange={(e) => upd("category", e.target.value)} />
                <datalist id="wc-category-list">
                  <option value="Computer Courses" />
                  <option value="Management Courses" />
                  <option value="Library & Information Science" />
                  <option value="Language Courses" />
                  <option value="Other Courses" />
                </datalist>
              </div>
              <div>
                <Label>Display Order</Label>
                <Input type="number" min="1" data-testid="wc-order" value={form.display_order} className="mt-1.5 h-10"
                  onChange={(e) => upd("display_order", e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Course Details</Label>
              <Textarea data-testid="wc-details" value={form.description} rows={6} className="mt-1.5"
                placeholder="About the course, what students will learn, objectives, benefits, practical training…"
                onChange={(e) => upd("description", e.target.value)} />
            </div>
            <ImageUpload label="Course Image / Thumbnail (optional)" testId="wc-image"
              value={form.image} onChange={(v) => upd("image", v)} aspect="wide" />
            <label className="flex items-center gap-2.5 text-sm text-slate-700">
              <input type="checkbox" data-testid="wc-active" checked={form.active}
                onChange={(e) => upd("active", e.target.checked)} className="w-4 h-4 accent-blue-800" />
              Active — show this course on the public website
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" data-testid="wc-cancel" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button data-testid="wc-save" onClick={save} disabled={saving} className="bg-blue-800 hover:bg-blue-900">
                {saving && <Loader2 size={14} className="animate-spin mr-2" />}
                {editing ? "Save Changes" : "Save Course"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="wc-del-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this course?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" will be permanently removed from the website course list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wc-del-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="wc-del-confirm" onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700">Delete Course</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
